export interface PrintDocumentField {
  label: string;
  value: string | number | null | undefined;
}

export interface PrintDocumentTable {
  columns: Array<{
    label: string;
    align?: 'left' | 'right';
  }>;
  rows: Array<Array<string | number | null | undefined>>;
}

export interface PrintDocumentSection {
  title?: string;
  fields?: PrintDocumentField[];
  table?: PrintDocumentTable;
  text?: string | null;
  /** Must already be loadable by the popup (e.g. a signed URL). */
  image?: { src: string; alt?: string } | null;
}

export interface PrintableDocument {
  title: string;
  number?: string | null;
  status?: string | null;
  subtitle?: string | null;
  sections: PrintDocumentSection[];
}

function escapeHtml(value: string | number | null | undefined): string {
  if (value == null || value === '') return '—';
  return String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

function renderSection(section: PrintDocumentSection): string {
  const heading = section.title ? `<h2>${escapeHtml(section.title)}</h2>` : '';
  const fields = section.fields?.length
    ? `<dl>${section.fields
        .map(
          (field) =>
            `<div><dt>${escapeHtml(field.label)}</dt><dd>${escapeHtml(field.value)}</dd></div>`,
        )
        .join('')}</dl>`
    : '';
  const table = section.table
    ? `<table>
        <thead><tr>${section.table.columns
          .map(
            (column) =>
              `<th class="${column.align === 'right' ? 'right' : ''}">${escapeHtml(column.label)}</th>`,
          )
          .join('')}</tr></thead>
        <tbody>${section.table.rows
          .map(
            (row) =>
              `<tr>${row
                .map(
                  (cell, index) =>
                    `<td class="${section.table?.columns[index]?.align === 'right' ? 'right' : ''}">${escapeHtml(cell)}</td>`,
                )
                .join('')}</tr>`,
          )
          .join('')}</tbody>
      </table>`
    : '';
  const text = section.text
    ? `<p class="notes">${escapeHtml(section.text).replaceAll('\n', '<br />')}</p>`
    : '';
  const image = section.image?.src
    ? `<img class="figure" src="${escapeHtml(section.image.src)}" alt="${section.image.alt ? escapeHtml(section.image.alt) : ''}" />`
    : '';
  return `<section>${heading}${fields}${table}${image}${text}</section>`;
}

/**
 * Opens a clean, printable record in a new window. The native print dialog
 * supports both physical printers and "Save as PDF" without a PDF dependency.
 * Returns false when the browser blocks the new window.
 */
export function printDocument(document: PrintableDocument): boolean {
  const popup = window.open('', '_blank');
  if (!popup) return false;
  popup.opener = null;

  const printedAt = new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date());
  const html = `<!doctype html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>${escapeHtml(document.number ? `${document.title} ${document.number}` : document.title)}</title>
    <style>
      @page { size: auto; margin: 0.55in; }
      * { box-sizing: border-box; }
      body {
        margin: 0;
        color: #18251d;
        font: 12px/1.45 Arial, Helvetica, sans-serif;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      header {
        display: flex;
        justify-content: space-between;
        gap: 24px;
        padding-bottom: 18px;
        border-bottom: 2px solid #244d36;
      }
      .brand { color: #244d36; font-size: 13px; font-weight: 700; letter-spacing: .08em; }
      h1 { margin: 5px 0 0; font-size: 25px; line-height: 1.15; }
      .number { margin-top: 5px; font: 600 13px ui-monospace, SFMono-Regular, Menlo, monospace; }
      .subtitle, .printed { color: #5e6d64; }
      .status {
        display: inline-block;
        margin-bottom: 8px;
        padding: 3px 9px;
        border: 1px solid #9caaa1;
        border-radius: 999px;
        font-size: 10px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: .04em;
      }
      .header-right { text-align: right; }
      section { margin-top: 20px; break-inside: avoid; }
      h2 {
        margin: 0 0 9px;
        color: #244d36;
        font-size: 12px;
        text-transform: uppercase;
        letter-spacing: .06em;
      }
      dl {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 12px 20px;
        margin: 0;
      }
      dt { color: #68776e; font-size: 10px; text-transform: uppercase; letter-spacing: .04em; }
      dd { margin: 2px 0 0; font-weight: 600; white-space: pre-wrap; }
      table { width: 100%; border-collapse: collapse; }
      th {
        padding: 7px 8px;
        border-bottom: 1px solid #87958c;
        background: #eef2ef;
        color: #4e5e54;
        font-size: 10px;
        text-align: left;
        text-transform: uppercase;
        letter-spacing: .03em;
      }
      td { padding: 8px; border-bottom: 1px solid #d8dfda; vertical-align: top; }
      .right { text-align: right; font-variant-numeric: tabular-nums; }
      .notes {
        margin: 0;
        padding: 11px 12px;
        border: 1px solid #d8dfda;
        border-radius: 5px;
        white-space: normal;
      }
      .figure {
        display: block;
        max-width: 100%;
        max-height: 3.4in;
        margin: 0 0 9px;
        border: 1px solid #d8dfda;
        border-radius: 5px;
        object-fit: contain;
      }
      footer {
        margin-top: 28px;
        padding-top: 9px;
        border-top: 1px solid #d8dfda;
        color: #76837b;
        font-size: 9px;
      }
      @media screen {
        body { max-width: 900px; margin: 32px auto; padding: 36px; box-shadow: 0 2px 18px #0002; }
      }
    </style>
  </head>
  <body>
    <header>
      <div>
        <div class="brand">VIRRO · ATLAS</div>
        <h1>${escapeHtml(document.title)}</h1>
        ${document.number ? `<div class="number">${escapeHtml(document.number)}</div>` : ''}
        ${document.subtitle ? `<div class="subtitle">${escapeHtml(document.subtitle)}</div>` : ''}
      </div>
      <div class="header-right">
        ${document.status ? `<div class="status">${escapeHtml(document.status)}</div>` : ''}
        <div class="printed">Printed ${escapeHtml(printedAt)}</div>
      </div>
    </header>
    ${document.sections.map(renderSection).join('')}
    <footer>Generated from Atlas · This copy reflects the record at the time shown above.</footer>
    <script>
      window.addEventListener('load', () => {
        window.setTimeout(() => {
          window.focus();
          window.print();
        }, 150);
      });
    </script>
  </body>
</html>`;

  popup.document.open();
  popup.document.write(html);
  popup.document.close();
  return true;
}
